import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  Grid,
  Button,
} from "@mui/material";
import Swal from "sweetalert2";

import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { loanStatementReportQuery } from "../../../data/Axios/queries";
import { tokens } from "../../../theme";
import { loanManagementUrl, reportsManagementUrl } from "../../../config";
import { buildLoanStatementHtml } from "./buildLoanStatementHtml";

const formatDate = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return Number.isNaN(d.getTime())
    ? "N/A"
    : d.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

const LoanStatement = () => {
  const [loanId, setLoanId] = useState("");
  const [loanDetails, setLoanDetails] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSearch = async () => {
    if (!loanId) {
      setSearchError("Please enter a Loan ID.");
      return;
    }

    setSearchError("");
    setIsSearching(true);
    try {
      const response = await fetchJsonDynamic(
        "allLoanSchedulesByLoanId",
        "parameters",
        { loanId: parseInt(loanId) },
        loanManagementUrl.uri
      );

      if (!response || response.error) {
        setLoanDetails(null);
        setSearchError(response?.error || "Failed to fetch loan data. Please try again.");
        return;
      }

      if (!response.results) {
        setLoanDetails(null);
        setSearchError("No loan found for that Loan ID.");
        return;
      }

      const data = JSON.parse(response.results);
      const application = data?.LoanApplications?.[0];

      if (!application) {
        setLoanDetails(null);
        setSearchError("No loan found for that Loan ID.");
        return;
      }

      setLoanDetails(application);
    } catch (error) {
      console.error("Error fetching loan data:", error);
      setLoanDetails(null);
      setSearchError("An error occurred while fetching the loan.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!loanDetails) return;

    const newTab = window.open("", "_blank");
    if (newTab) {
      newTab.document.write("<p>Generating statement...</p>");
    }

    try {
      setIsLoading(true);
      const query = loanStatementReportQuery(parseInt(loanDetails.Id));
      const response = await graphqlQuery(query, reportsManagementUrl.uri);

      if (response?.error) {
        newTab?.close();
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to generate the loan statement. Please try again later.",
        });
        return;
      }

      const html = buildLoanStatementHtml({
        rawResults: response?.results,
        generatedAt: new Date(),
      });

      if (newTab) {
        newTab.document.open();
        newTab.document.write(html);
        newTab.document.close();
      }
    } catch (error) {
      newTab?.close();
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while generating the loan statement.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fields = loanDetails
    ? [
        { label: "Loan ID", value: `${loanDetails.Id || "N/A"}` },
        {
          label: "Loan Amount",
          value: `KSH ${loanDetails.LoanAmount ?? 0.0}`,
        },
        {
          label: "Principal Amount",
          value: `KSH ${loanDetails.PrincipalAmount ?? 0.0}`,
        },
        {
          label: "Borrow Date",
          value: formatDate(loanDetails.DateCreated),
        },
        {
          label: "Amount Disbursed",
          value: `KSH ${loanDetails.AmountToDisburse ?? 0.0}`,
        },
        {
          label: "Interest Amount",
          value: `KSH ${loanDetails.InterestAmount ?? 0.0}`,
        },
        {
          label: "Expected Clear Date",
          value: formatDate(loanDetails.ExpectedClearDate),
        },
      ]
    : [];

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4">Loan Statement</Typography>
      <Box sx={{ display: "flex", gap: 2, marginTop: 2, alignItems: "flex-start" }}>
        <TextField
          label="Enter Loan ID"
          placeholder="e.g. 181706"
          variant="outlined"
          value={loanId}
          onChange={(e) => setLoanId(e.target.value)}
          error={!!searchError}
          helperText={searchError}
          sx={{ maxWidth: 300 }}
          InputProps={{
            endAdornment: isSearching && <CircularProgress size={20} />,
          }}
        />
        <Button variant="contained" onClick={handleSearch} disabled={isSearching}>
          {isSearching ? <CircularProgress size={20} /> : "Search"}
        </Button>
      </Box>

      {loanDetails && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            padding: { xs: 1, sm: 2 },
            maxWidth: "1200px",
            marginTop: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                textAlign: { xs: "center", sm: "left" },
                mb: 3,
                fontSize: "1rem",
              }}
            >
              Loan Details
            </Typography>
            {isSmallScreen ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  background: colors.primary[200],
                  borderRadius: 2,
                  padding: 2,
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                }}
              >
                {fields.map((field, index) => (
                  <Grid
                    container
                    key={index}
                    sx={{
                      borderBottom:
                        index !== fields.length - 1 ? "1px solid #ccc" : "none",
                      py: 1,
                    }}
                  >
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        {field.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ textAlign: "right" }}>
                        {field.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  background: `${colors.primary[200]} !important`,
                  borderRadius: 2,
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                  overflowX: "auto",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      {fields.map((field, index) => (
                        <TableCell
                          key={index}
                          sx={{
                            background: colors.primary[400],
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                          }}
                        >
                          {field.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {fields.map((field, index) => (
                        <TableCell key={index} sx={{ fontSize: "0.73rem" }}>
                          {field.value}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateReport}
              disabled={isLoading}
              sx={{
                background: colors.blueAccent[500],
                "&:hover": { background: colors.blueAccent[600] },
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : "Generate Statement"}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LoanStatement;
